/**
 * DocumentsTab — every DocuSign envelope tied to this property.
 *
 * Reads /api/docusign/envelopes?lead_id={property.id} (property.id ===
 * lead.id post-unification), shows status, template, timestamp, and a
 * link to open the envelope in DocuSign. New envelopes can be launched
 * straight from the top-right "Send for Signature" button on the
 * Command Center header — the tab is intentionally read-only.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Loader2, FileText, ExternalLink, Clock, CheckCircle2, AlertCircle,
  Send, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_META = {
  sent:      { label: "Sent",      icon: Send,          color: "#F59E0B" },
  delivered: { label: "Delivered", icon: Clock,         color: "#3B82F6" },
  completed: { label: "Signed",    icon: CheckCircle2,  color: "#10B981" },
  declined:  { label: "Declined",  icon: AlertCircle,   color: "#DC2626" },
  voided:    { label: "Voided",    icon: AlertCircle,   color: "#94A3B8" },
};

export default function DocumentsTab({ property }) {
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/docusign/envelopes?lead_id=${property.id}`);
      setEnvelopes(data.items || data.envelopes || data || []);
    } catch {
      setEnvelopes([]);
    } finally {
      setLoading(false);
    }
  }, [property.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="tab-panel-documents">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <FileText className="w-3.5 h-3.5" /> DocuSign Envelopes
          </div>
          <Link
            to="/v3/crm/contracts"
            data-testid="documents-contracts-link"
            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
          >
            All contracts <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-slate-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading envelopes…
          </div>
        ) : envelopes.length === 0 ? (
          <div className="p-8 text-center" data-testid="documents-empty">
            <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <div className="text-slate-900 font-semibold text-sm">No documents yet</div>
            <div className="text-[12px] text-slate-500 mt-1 max-w-sm mx-auto">
              Click <b>Send for Signature</b> in the top-right of this property
              to fire a DocuSign envelope. Signed contracts + status updates land here in real-time.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {envelopes.map((env, i) => {
              const status = STATUS_META[String(env.status || "").toLowerCase()] || STATUS_META.sent;
              const Icon = status.icon;
              return (
                <li key={env.id || env.envelope_id || i}
                    data-testid={`document-envelope-${i}`}
                    className="px-4 py-3 flex items-center gap-3">
                  <span
                    className="rounded-md p-1.5 shrink-0"
                    style={{ backgroundColor: `${status.color}12`, borderColor: `${status.color}30` }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: status.color }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">
                      {env.template_name || env.subject || env.template_key || "DocuSign envelope"}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {env.status_updated_at || env.created_at || env.sent_at || "—"}
                    </div>
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-widest font-black shrink-0"
                    style={{ color: status.color }}
                  >{status.label}</span>
                  {String(env.status || "").toLowerCase() === "completed" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/docusign/envelopes/${env.docusign_envelope_id || env.envelope_id || env.id}/document`}
                      target="_blank" rel="noreferrer"
                      data-testid={`document-envelope-${i}-download`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10.5px] font-bold px-2 py-1 hover:bg-emerald-100"
                      title="Download signed PDF"
                    >
                      <FileText className="w-3 h-3" /> PDF
                    </a>
                  )}
                  {env.docusign_url && (
                    <a
                      href={env.docusign_url} target="_blank" rel="noreferrer"
                      className="text-slate-400 hover:text-slate-700"
                      title="Open in DocuSign"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
